import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { timeAgo, getInitials, avatarColor } from "./utils";

const CommentHistory = ({
  history = [],
  onNavigateToParent,
  parentRequestCode,
  onNavigateToRequest,
}) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";

  // Sort with most recently added history at the top
  const sortedHistory = React.useMemo(() => {
    if (!history || !Array.isArray(history)) return [];
    return [...history].sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );
  }, [history]);

  if (!sortedHistory || sortedHistory.length === 0) {
    return (
      <Box sx={{
        padding: "16px",
        background: t.surface2,
        border: `1px solid ${t.border2}`,
        borderRadius: "4px",
        textAlign: "center",
      }}>
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3, letterSpacing: "0.08em" }}>
          No linked comments or notes found for this request chain.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {sortedHistory.map((item, idx) => {
        const isCurrent = item.isCurrentRequest;
        const isParent = item.relation === "Parent";
        const isChild = item.relation === "Child";
        const isRejection = item.noteType === "Rejection Reason";
        const isApproval = item.noteType === "Approval Note";

        const relationColor = isCurrent
          ? t.accent
          : isParent
          ? t.gold
          : "#a78bfa";

        const relationBg = isCurrent
          ? "rgba(40,179,180,0.12)"
          : isParent
          ? "rgba(214,223,39,0.12)"
          : "rgba(122,90,189,0.12)";

        const isNavigable = !isCurrent && (
          (onNavigateToRequest && item.requestId) ||
          (isParent && onNavigateToParent && item.requestCode === parentRequestCode)
        );

        return (
          <Box
            key={`${item.requestId}-${item.createdAt}-${idx}`}
            sx={{
              background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
              border: `1px solid ${isCurrent ? (isDark ? "rgba(40,179,180,0.3)" : "rgba(40,179,180,0.4)") : t.border2}`,
              borderRadius: "4px",
              padding: "10px 12px",
              position: "relative",
            }}
          >
            {/* Top header row */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "6px", flexWrap: "wrap", gap: "6px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {/* Avatar */}
                <Box sx={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  background: `${avatarColor(item.authorName)}22`,
                  border: `1px solid ${avatarColor(item.authorName)}66`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "8px",
                  fontWeight: 700,
                  color: avatarColor(item.authorName),
                }}>
                  {getInitials(item.authorName)}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", fontWeight: 600, color: t.text }}>
                    {item.authorName}
                  </Typography>
                  <Typography sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "8px",
                    px: "5px",
                    py: "1px",
                    borderRadius: "2px",
                    background: t.surface3,
                    color: t.text3,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}>
                    {item.authorRole}
                  </Typography>
                </Box>
              </Box>

              {/* Badges on right: Relation & Request Code */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "8px",
                  fontWeight: 600,
                  px: "5px",
                  py: "1px",
                  borderRadius: "2px",
                  background: relationBg,
                  color: relationColor,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}>
                  {item.relation}
                </Box>

                <Typography
                  onClick={isNavigable ? () => {
                    if (onNavigateToRequest) {
                      onNavigateToRequest(item);
                    } else if (isParent && onNavigateToParent) {
                      onNavigateToParent();
                    }
                  } : undefined}
                  sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "9px",
                    color: isNavigable ? t.accent : t.text3,
                    cursor: isNavigable ? "pointer" : "default",
                    letterSpacing: "0.06em",
                    textDecoration: isNavigable ? "underline" : "none",
                  }}
                  title={isNavigable ? `Navigate to ${item.requestCode}` : undefined}
                >
                  {item.requestCode}
                </Typography>
              </Box>
            </Box>

            {/* Note Type & Timestamp row */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "6px" }}>
              <Typography sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "8px",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: isRejection ? "#c84040" : isApproval ? t.accent : t.text3,
                fontWeight: isRejection || isApproval ? 600 : 400,
              }}>
                {item.noteType}
              </Typography>

              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8.5px", color: t.text3 }}>
                {timeAgo(item.createdAt)}
              </Typography>
            </Box>

            {/* Note text box */}
            <Box sx={{
              background: isRejection
                ? "rgba(200,64,64,0.07)"
                : t.surface2,
              border: `1px solid ${isRejection ? "rgba(200,64,64,0.2)" : t.border2}`,
              borderRadius: "3px",
              padding: "8px 10px",
            }}>
              <Typography sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "11.5px",
                color: t.text2,
                lineHeight: 1.55,
                fontWeight: 300,
                whiteSpace: "pre-wrap",
              }}>
                {item.noteText}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CommentHistory;
